import * as React from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { JSONSchema7, JSONSchema7Definition } from "json-schema";
import { PhoneSelector } from "@/components/PhoneSelector";
import { EmailInputWithSlug } from "@/components/EmailInputWithSlug";
import { CompanySelector } from "@/components/CompanySelector";
import { UserSelector } from "@/components/UserSelector";
import { ItemSelector } from "@/components/ItemSelector";
import { LocationSelector } from "@/components/LocationSelector";
import { OpportunitySelector } from "@/components/OpportunitySelector";
import { OperationSelector } from "@/components/OperationSelector";
import { NumberTextInput } from "@/components/NumberTextInput";
import { keyToLabel } from "@/lib/utils";
import { JSONSchemaDefinition, ajv } from "@forgepoint/shared";
import { CronTimerField } from "@/components/cron/CronTimerField";
import type { CronTimerValue } from "@/components/cron/cronUtils";
import { Plus } from "lucide-react";
import { arrayMove } from "@dnd-kit/sortable";
import { AssetSelectorField } from "@/components/AssetSelectorField";
import { ConnectionSelector } from "@/components/ConnectionSelector";
import { OpportunityStageSelector } from "@/components/OpportunityStageSelector";
import {
  JSONValue,
  JSONObject,
  isJSONObject,
  JSONSchemaFieldTemplateProps,
} from "@/components/jsonSchemaForm/types";
import {
  isSectionContainer,
  isNumberableInput,
} from "@/components/jsonSchemaForm/fieldHelpers";
import { InputWithSuffix } from "@/components/jsonSchemaForm/InputWithSuffix";
import { SortableArrayField } from "@/components/jsonSchemaForm/SortableArrayField";
import { JSONSchemaFieldTemplate } from "@/components/jsonSchemaForm/JSONSchemaFieldTemplate";

interface JSONSchemaFormProps<FormContextType> {
  schema: JSONSchema7;
  formData: JSONValue;
  onChange: (data: JSONValue, shouldReloadSchema: boolean) => void;
  onSubmit?: (data: JSONValue) => void;
  className?: string;
  disabled?: boolean;
  readonly?: boolean;
  showSubmitButton?: boolean;
  onClickAsset?: (assetId: string) => void;
  fieldTemplate?: React.ComponentType<
    JSONSchemaFieldTemplateProps<FormContextType>
  >;
  formContext?: FormContextType;
  /**
   * When true, top-level input fields get a sequential number in a left gutter
   * (read-only calculations and section containers are skipped). Helps users
   * count and reference fields in long forms.
   */
  numberFields?: boolean;
  /**
   * Top-level field keys that have been modified since the last save. They get
   * an accent dot in the number gutter, so a batch of pending edits is visible
   * at a glance. Requires `numberFields`.
   */
  modifiedFields?: Set<string>;
}

export function JSONSchemaForm<FormContextType>({
  schema,
  formData,
  onChange,
  onSubmit,
  className,
  disabled = false,
  readonly = false,
  showSubmitButton = false,
  onClickAsset,
  fieldTemplate: FieldTemplate = JSONSchemaFieldTemplate,
  formContext,
  numberFields = false,
  modifiedFields,
}: JSONSchemaFormProps<FormContextType>) {
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [selectedOptionIndex, setSelectedOptionIndex] = React.useState<
    Record<string, number>
  >({});

  function getSchemaType(schema: JSONSchema7Definition): string | undefined {
    if (typeof schema === "boolean") {
      return undefined;
    }
    if (Array.isArray(schema.type)) {
      return schema.type[0];
    }
    return schema.type;
  }

  function getSchemaLabel(schema: JSONSchema7Definition): string {
    if (typeof schema === "boolean") {
      return schema ? "Any" : "None";
    }

    const type = getSchemaType(schema);
    const schemaTitle = schema.title;

    if (type === "object" && schemaTitle) {
      return schemaTitle;
    }

    switch (type) {
      case "string":
        return "Text";
      case "number":
        return "Number";
      case "integer":
        return "Integer";
      case "boolean":
        return "True/False";
      case "null":
        return "Set to Empty";
      case "object":
        return "Object";
      case "array":
        return "List";
      default:
        if (schemaTitle) {
          return schemaTitle;
        }
        if (schema.enum && schema.enum.length > 0) {
          return "Select";
        }
        return "Value";
    }
  }

  function getValueType(value: JSONValue): string | undefined {
    if (value === null) {
      return "null";
    }
    if (Array.isArray(value)) {
      return "array";
    }
    if (typeof value === "object") {
      return "object";
    }
    if (typeof value === "boolean") {
      return "boolean";
    }
    if (typeof value === "number") {
      return Number.isInteger(value) ? "integer" : "number";
    }
    if (typeof value === "string") {
      return "string";
    }
    return undefined;
  }

  function findMatchingOptionIndex(
    optionSchemas: JSONSchema7Definition[],
    value: JSONValue,
  ): number {
    for (let i = 0; i < optionSchemas.length; i++) {
      const subSchema = optionSchemas[i];
      if (subSchema === true) {
        return i;
      }
      if (subSchema === false) {
        continue;
      }
      const validate = ajv.compile(subSchema);
      if (validate(value)) {
        return i;
      }
    }

    const valueType = getValueType(value);
    for (let i = 0; i < optionSchemas.length; i++) {
      const subSchema = optionSchemas[i];
      if (typeof subSchema === "boolean") {
        continue;
      }
      const schemaType = getSchemaType(subSchema);
      if (schemaType === valueType) {
        return i;
      }
      if (valueType === "integer" && schemaType === "number") {
        return i;
      }
    }

    // If no match found, return 0
    return 0;
  }

  function getDefaultValue(schema: JSONSchema7Definition): JSONValue {
    if (typeof schema === "boolean") {
      return schema;
    }

    if (schema.default !== undefined) {
      return schema.default;
    }
    if (schema.const !== undefined) {
      return schema.const;
    }

    const type = Array.isArray(schema.type) ? schema.type[0] : schema.type;

    switch (type) {
      case "object": {
        const obj: JSONObject = {};
        if (schema.properties) {
          Object.entries(schema.properties).forEach(([key, propSchema]) => {
            if (typeof propSchema === "boolean") {
              return;
            }
            obj[key] = getDefaultValue(propSchema);
          });
        }
        return obj;
      }
      case "array":
        return [];
      case "string":
        return "";
      case "number":
      case "integer":
        return schema.minimum ?? 0;
      case "boolean":
        return false;
      case "null":
        return null;
      default:
        return null;
    }
  }

  function matchesSchema(
    value: JSONValue,
    targetSchema: JSONSchema7Definition,
  ) {
    if (typeof targetSchema === "boolean") {
      return targetSchema;
    }
    const validate = ajv.compile(targetSchema);
    return validate(value);
  }

  function mergeValuesForSchemaChange(
    existingValue: JSONValue,
    targetSchema: JSONSchema7Definition,
  ): JSONValue {
    if (typeof targetSchema === "boolean") {
      return targetSchema;
    }

    const defaultValue = getDefaultValue(targetSchema);
    const targetType = getSchemaType(targetSchema);

    if (targetType !== "object") {
      if (
        existingValue !== undefined &&
        matchesSchema(existingValue, targetSchema)
      ) {
        return existingValue;
      }
      return defaultValue;
    }

    if (!isJSONObject(defaultValue)) {
      return defaultValue;
    }

    const mergedValue: JSONObject = structuredClone(defaultValue);
    if (!isJSONObject(existingValue) || !targetSchema.properties) {
      return mergedValue;
    }

    Object.entries(targetSchema.properties).forEach(([propKey, propSchema]) => {
      if (typeof propSchema === "boolean") {
        return;
      }
      const existingPropValue = existingValue[propKey];
      if (existingPropValue === undefined) {
        return;
      }
      if (matchesSchema(existingPropValue, propSchema)) {
        mergedValue[propKey] = mergeValuesForSchemaChange(
          existingPropValue,
          propSchema,
        );
      }
    });

    return mergedValue;
  }

  function validateForm(data: JSONValue): boolean {
    const validate = ajv.compile(schema);
    const isValid = validate(data);

    if (!isValid && validate.errors) {
      const newErrors: Record<string, string> = {};

      validate.errors.forEach((error) => {
        const path = error.instancePath
          ? error.instancePath.substring(1).replace(/\//g, ".")
          : "";

        if (!newErrors[path]) {
          let message = error.message || "Invalid value";

          switch (error.keyword) {
            case "required":
              message = `${error.params.missingProperty} is required`;
              newErrors[error.params.missingProperty] = message;
              return;
            case "minLength":
              message = `Minimum length is ${error.params.limit}`;
              break;
            case "maxLength":
              message = `Maximum length is ${error.params.limit}`;
              break;
            case "minimum":
              message = `Minimum value is ${error.params.limit}`;
              break;
            case "maximum":
              message = `Maximum value is ${error.params.limit}`;
              break;
            case "pattern":
              message = "Invalid format";
              break;
            case "format":
              message = `Invalid ${error.params.format} format`;
              break;
            case "type":
              message = `Must be a ${error.params.type}`;
              break;
            case "enum":
              message = `Must be one of the allowed values`;
              break;
            case "multipleOf":
              message = `Must be a multiple of ${error.params.multipleOf}`;
              break;
          }

          if (path) {
            newErrors[path] = message;
          }
        }
      });

      setErrors(newErrors);
      return false;
    }

    setErrors({});
    return true;
  }

  React.useEffect(() => {
    if (formData) {
      validateForm(formData);
    }
  }, [formData]);

  function updateFormData(
    path: string[],
    value: JSONValue,
    shouldReloadSchema: boolean,
  ) {
    if (path.length === 0) {
      onChange?.(value, shouldReloadSchema);
      validateForm(value);
      return;
    }

    type JSONContainer = JSONObject | JSONValue[];

    const isJSONContainer = (
      candidate: JSONValue | undefined,
    ): candidate is JSONContainer => {
      return (
        candidate !== null &&
        candidate !== undefined &&
        typeof candidate === "object"
      );
    };

    const clonedData = structuredClone(formData);
    const newData: JSONContainer = isJSONContainer(clonedData)
      ? clonedData
      : {};
    let current: JSONContainer = newData;

    const getNextContainer = (
      existingValue: JSONValue | undefined,
      nextKey: string,
    ): JSONContainer => {
      if (isJSONContainer(existingValue)) {
        return existingValue;
      }
      return Number.isNaN(Number(nextKey)) ? {} : [];
    };

    for (let i = 0; i < path.length - 1; i++) {
      const key = path[i];
      const nextKey = path[i + 1];

      if (Array.isArray(current)) {
        const index = Number(key);
        if (Number.isNaN(index)) {
          return;
        }
        const container = getNextContainer(current[index], nextKey);
        current[index] = container;
        current = container;
      } else {
        const container = getNextContainer(current[key], nextKey);
        current[key] = container;
        current = container;
      }
    }

    if (path.length > 0) {
      const finalKey = path[path.length - 1];
      if (Array.isArray(current)) {
        const index = Number(finalKey);
        if (Number.isNaN(index)) {
          return;
        }
        current[index] = value;
      } else {
        current[finalKey] = value;
      }
    }

    onChange?.(newData, shouldReloadSchema);
    validateForm(newData);
  }

  function getValueAtPath(
    data: JSONValue,
    path: string[],
  ): JSONValue | undefined {
    if (path.length === 0) {
      return data;
    }

    let current: JSONValue | undefined = data;
    for (const key of path) {
      if (current === null || current === undefined) {
        return undefined;
      }
      if (typeof current === "object" && !Array.isArray(current)) {
        current = current[key];
      } else if (Array.isArray(current)) {
        const index = parseInt(key, 10);
        if (!isNaN(index)) {
          current = current[index];
        } else {
          return undefined;
        }
      } else {
        return undefined;
      }
    }
    return current;
  }

  function renderField(
    schema: JSONSchemaDefinition,
    path: string[] = [],
    key?: string,
    parentRequired?: string[],
    parentIsArrayItem = false,
    hideLabel = false,
  ): React.ReactNode {
    if (typeof schema === "boolean") {
      return null;
    }

    const fieldId = path.join(".");
    const value = getValueAtPath(formData, path);
    const error = errors[fieldId];
    const type = Array.isArray(schema.type) ? schema.type[0] : schema.type;
    const isRequired = parentRequired?.includes(key!) || false;
    const formattedLabel = hideLabel
      ? undefined
      : (schema.title ?? (key ? keyToLabel(key) : undefined));
    const shouldReloadSchema = schema.shouldReload ?? false;
    const inputCls = "h-8";
    const selectCls = "h-8";

    if (schema.isCronTimer) {
      return (
        <FieldTemplate
          key={fieldId}
          id={fieldId}
          label={formattedLabel}
          description={schema.description}
          required={isRequired}
          error={error}
          schemaPath={path}
          formContext={formContext}
          defaultValue={getDefaultValue(schema)}
          schema={schema}
          currValue={value}
        >
          <CronTimerField
            value={(value as CronTimerValue | undefined) ?? undefined}
            onChange={(nextValue) =>
              updateFormData(path, nextValue as JSONObject, shouldReloadSchema)
            }
            disabled={disabled || readonly}
          />
        </FieldTemplate>
      );
    }

    if (schema.const !== undefined) {
      return (
        <div key={fieldId} className="text-sm text-muted-foreground">
          {String(schema.const)}
        </div>
      );
    }

    if (schema.format === "fpCalculation") {
      // Read-only, model-computed field — values arrive as numeric strings, so
      // suppress schema (e.g. type "number") validation errors as user noise.
      const calcTitle =
        typeof schema.title === "string" ? schema.title : undefined;
      const calcSuffix = schema.suffix;
      const calcLabel = hideLabel ? undefined : (calcTitle ?? formattedLabel);
      return (
        <FieldTemplate
          key={fieldId}
          id={fieldId}
          label={calcLabel}
          description={schema.description}
          required={false}
          error={undefined}
          schemaPath={path}
          formContext={formContext}
          defaultValue={getDefaultValue(schema)}
          schema={schema}
          currValue={value}
        >
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="cursor-help">
                  <InputWithSuffix suffix={calcSuffix}>
                    <Input
                      id={fieldId}
                      className="pointer-events-none h-full rounded-none border-0 bg-transparent italic shadow-none focus-visible:ring-0"
                      value={
                        value === undefined || value === null
                          ? ""
                          : String(value)
                      }
                      placeholder="Calculated by price model"
                      readOnly
                      disabled
                    />
                  </InputWithSuffix>
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-56">
                Automatically calculated from your other inputs.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </FieldTemplate>
      );
    }

    if (
      (schema.anyOf && Array.isArray(schema.anyOf)) ||
      (schema.oneOf && Array.isArray(schema.oneOf))
    ) {
      const schemas = schema.anyOf ?? schema.oneOf;
      if (!schemas || schemas.length === 0) {
        return null;
      }
      const optionType = schema.anyOf ? "anyOf" : "oneOf";
      const selectionKey = `${optionType}_${fieldId}`;
      const detectedIndex = findMatchingOptionIndex(schemas, value);
      const currentIndex = selectedOptionIndex[selectionKey] ?? detectedIndex;
      const safeIndex = Math.min(
        Math.max(currentIndex, 0),
        Math.max(schemas.length - 1, 0),
      );
      const selectedSchema = schemas[safeIndex];
      const hideOptionLabel =
        typeof selectedSchema !== "boolean" &&
        getSchemaType(selectedSchema) === "object";

      return (
        <FieldTemplate
          key={fieldId}
          id={fieldId}
          label={formattedLabel}
          description={schema.description}
          required={isRequired}
          error={error}
          schemaPath={path}
          formContext={formContext}
          defaultValue={getDefaultValue(schema)}
          schema={schema}
          currValue={value}
        >
          <div className="space-y-2">
            <Select
              value={String(safeIndex)}
              onValueChange={(newIndexValue) => {
                if (disabled || readonly) {
                  return;
                }
                const newIndex = parseInt(newIndexValue, 10);
                setSelectedOptionIndex((prev) => ({
                  ...prev,
                  [selectionKey]: newIndex,
                }));
                const newlySelectedSchema = schemas[newIndex];
                const nextValue = mergeValuesForSchemaChange(
                  value,
                  newlySelectedSchema,
                );
                updateFormData(path, nextValue, shouldReloadSchema);
              }}
              disabled={disabled || readonly}
            >
              <SelectTrigger className={selectCls}>
                <SelectValue placeholder="Select option" />
              </SelectTrigger>
              <SelectContent>
                {schemas.map((subSchema, index) => (
                  <SelectItem key={index} value={String(index)}>
                    {getSchemaLabel(subSchema)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {renderField(
              selectedSchema,
              path,
              undefined,
              parentRequired,
              parentIsArrayItem,
              hideOptionLabel,
            )}
          </div>
        </FieldTemplate>
      );
    }

    if (schema.enum) {
      const enumNames = Array.isArray(schema.enumNames)
        ? schema.enumNames
        : undefined;
      return (
        <FieldTemplate
          key={fieldId}
          id={fieldId}
          label={formattedLabel}
          description={schema.description}
          required={isRequired}
          error={error}
          schemaPath={path}
          formContext={formContext}
          defaultValue={getDefaultValue(schema)}
          schema={schema}
          currValue={value}
        >
          <Select
            value={String(value ?? "")}
            onValueChange={(val) => {
              const typedValue =
                schema.enum!.find((e) => String(e) === val) ?? val;
              updateFormData(path, typedValue, shouldReloadSchema);
            }}
            disabled={disabled || readonly}
          >
            <SelectTrigger id={fieldId} className={selectCls}>
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              {schema.enum.map((option, index) => {
                const optionLabel = enumNames?.[index] ?? String(option);
                return (
                  <SelectItem key={String(option)} value={String(option)}>
                    {optionLabel}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </FieldTemplate>
      );
    }

    switch (type) {
      case "object": {
        const propertyEntries = schema.properties
          ? Object.entries(schema.properties)
          : [];
        const propertyOrder = (
          schema as JSONSchema7 & { propertyOrder?: string[] }
        ).propertyOrder;
        if (propertyOrder) {
          const orderMap = new Map(propertyOrder.map((key, idx) => [key, idx]));
          propertyEntries.sort(
            (a, b) =>
              (orderMap.get(a[0]) ?? Infinity) -
              (orderMap.get(b[0]) ?? Infinity),
          );
        }
        // Number top-level input fields in a left gutter. Non-inputs (read-only
        // calcs + section containers) render flush, so section headers aren't
        // pushed inward to align with the numbered fields.
        const numbered = numberFields && path.length === 0;
        let inputCounter = 0;
        const subFields = propertyEntries.map(([propKey, propSchema]) => {
          const field = renderField(
            propSchema,
            [...path, propKey],
            propKey,
            schema.required,
            false,
            false,
          );
          // Section containers render flush; leaf fields sit in the gutter
          // (numbered for inputs, blank but aligned for read-only calcs).
          if (!numbered || isSectionContainer(propSchema)) {
            return <div key={propKey}>{field}</div>;
          }
          const num = isNumberableInput(propSchema) ? ++inputCounter : null;
          return (
            <div key={propKey} className="flex items-center gap-2">
              <span className="flex w-6 shrink-0 select-none items-center justify-end gap-1 text-xs tabular-nums text-muted-foreground/60">
                {modifiedFields?.has(propKey) && (
                  <span
                    className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"
                    aria-label="Modified since last save"
                  />
                )}
                {num ?? ""}
              </span>
              <div className="min-w-0 flex-1">{field}</div>
            </div>
          );
        });

        const wrapperClassName = cn(
          "divide-y divide-border/40",
          path.length > 0 && !parentIsArrayItem
            ? "border-l-2 border-border/60 pl-4"
            : undefined,
          parentIsArrayItem && "[&>div]:!py-1",
        );

        return path.length > 0 ? (
          <FieldTemplate
            key={fieldId}
            id={fieldId}
            label={formattedLabel}
            description={schema.description}
            required={isRequired}
            error={error}
            schemaPath={path}
            formContext={formContext}
            defaultValue={getDefaultValue(schema)}
            schema={schema}
            currValue={value}
          >
            <div className={wrapperClassName}>{subFields}</div>
          </FieldTemplate>
        ) : (
          <div className="divide-y divide-border/40">{subFields}</div>
        );
      }

      case "array": {
        const items = Array.isArray(value) ? value : [];
        const arrayItemSchema =
          typeof schema.items === "object" && !Array.isArray(schema.items)
            ? schema.items
            : {};
        // Stable-ish ids for dnd-kit. Index-based is fine: on drop the data
        // array is reordered and re-rendered, so indices reassign in order.
        const itemIds = items.map((_item, index) => `${fieldId}.${index}`);
        const itemRows = items.map((_item, index) =>
          renderField(
            arrayItemSchema,
            [...path, String(index)],
            undefined,
            parentRequired,
            true,
            getSchemaType(arrayItemSchema) === "object",
          ),
        );
        return (
          <FieldTemplate
            key={fieldId}
            id={fieldId}
            label={formattedLabel}
            description={schema.description}
            required={isRequired}
            error={error}
            schemaPath={path}
            formContext={formContext}
            defaultValue={getDefaultValue(schema)}
            schema={schema}
            currValue={value}
          >
            <div className="space-y-1.5">
              <SortableArrayField
                dndId={`dnd-${fieldId}`}
                ids={itemIds}
                rows={itemRows}
                showControls={!disabled && !readonly}
                onReorder={(oldIndex, newIndex) =>
                  updateFormData(
                    path,
                    arrayMove(items, oldIndex, newIndex),
                    shouldReloadSchema,
                  )
                }
                onRemove={(index) => {
                  const newItems = [...items];
                  newItems.splice(index, 1);
                  updateFormData(path, newItems, shouldReloadSchema);
                }}
              />
              {!readonly && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    const newItems = [
                      ...items,
                      getDefaultValue(
                        typeof schema.items === "object" &&
                          !Array.isArray(schema.items)
                          ? schema.items
                          : {},
                      ),
                    ];
                    updateFormData(path, newItems, shouldReloadSchema);
                  }}
                  disabled={
                    disabled ||
                    readonly ||
                    (schema.maxItems !== undefined &&
                      items.length >= schema.maxItems)
                  }
                >
                  <Plus className="mr-1.5 h-3.5 w-3.5" />
                  {typeof schema.items === "object" &&
                  !Array.isArray(schema.items) &&
                  schema.items.title
                    ? `Add ${schema.items.title}`
                    : "Add Item"}
                </Button>
              )}
            </div>
          </FieldTemplate>
        );
      }

      case "string": {
        if (schema.appConnectionConfig) {
          return (
            <FieldTemplate
              key={fieldId}
              id={fieldId}
              label={formattedLabel}
              description={schema.description}
              required={isRequired}
              error={error}
              schemaPath={path}
              formContext={formContext}
              defaultValue={getDefaultValue(schema)}
              schema={schema}
              currValue={value}
            >
              <ConnectionSelector
                appId={schema.appConnectionConfig.appId}
                providerId={schema.appConnectionConfig.providerId}
                connectionId={typeof value === "string" ? value : undefined}
                onSelect={(connectionId) =>
                  updateFormData(path, connectionId, shouldReloadSchema)
                }
                disabled={disabled || readonly}
              />
            </FieldTemplate>
          );
        }

        if (schema.format === "fpAsset") {
          return (
            <FieldTemplate
              key={fieldId}
              id={fieldId}
              label={formattedLabel}
              description={schema.description}
              required={isRequired}
              error={error}
              schemaPath={path}
              formContext={formContext}
              defaultValue={getDefaultValue(schema)}
              schemaFormat={schema.format}
              schema={schema}
              currValue={value}
            >
              <AssetSelectorField
                value={typeof value === "string" ? value : undefined}
                onChange={(assetId) =>
                  updateFormData(path, assetId, shouldReloadSchema)
                }
                disabled={disabled || readonly}
                onClickAsset={onClickAsset}
              />
            </FieldTemplate>
          );
        }

        if (schema.format === "fpTextarea") {
          return (
            <FieldTemplate
              key={fieldId}
              id={fieldId}
              label={formattedLabel}
              description={schema.description}
              required={isRequired}
              error={error}
              schemaPath={path}
              formContext={formContext}
              defaultValue={getDefaultValue(schema)}
              schemaFormat={schema.format}
              schema={schema}
              currValue={value}
            >
              <Textarea
                id={fieldId}
                value={typeof value === "string" ? value : ""}
                onChange={(e) =>
                  updateFormData(path, e.target.value, shouldReloadSchema)
                }
                disabled={disabled || readonly}
                maxLength={schema.maxLength}
                className="text-xs min-h-[60px]"
              />
            </FieldTemplate>
          );
        }

        // Check for custom fpPhoneNumber format
        if (schema.format === "fpPhoneNumber") {
          return (
            <FieldTemplate
              key={fieldId}
              id={fieldId}
              label={formattedLabel}
              description={schema.description}
              required={isRequired}
              error={error}
              schemaPath={path}
              formContext={formContext}
              defaultValue={getDefaultValue(schema)}
              schema={schema}
              currValue={value}
            >
              <PhoneSelector
                value={typeof value === "string" ? value : ""}
                onChange={(phoneId) =>
                  updateFormData(path, phoneId, shouldReloadSchema)
                }
                disabled={disabled || readonly}
                placeholder="Select a phone number"
              />
            </FieldTemplate>
          );
        }

        // Check for custom fpEmailAddress format
        if (schema.format === "fpEmailAddress") {
          return (
            <FieldTemplate
              key={fieldId}
              id={fieldId}
              label={formattedLabel}
              description={schema.description}
              required={isRequired}
              error={error}
              schemaPath={path}
              formContext={formContext}
              defaultValue={getDefaultValue(schema)}
              schema={schema}
              currValue={value}
            >
              <EmailInputWithSlug
                value={typeof value === "string" ? value : ""}
                onChange={(emailPrefix) =>
                  updateFormData(path, emailPrefix, shouldReloadSchema)
                }
                disabled={disabled || readonly}
                placeholder="Enter email prefix"
              />
            </FieldTemplate>
          );
        }

        if (schema.format === "fpCompany") {
          return (
            <FieldTemplate
              key={fieldId}
              id={fieldId}
              label={formattedLabel}
              description={schema.description}
              required={isRequired}
              error={error}
              schemaPath={path}
              formContext={formContext}
              defaultValue={getDefaultValue(schema)}
              schema={schema}
              currValue={value}
            >
              <CompanySelector
                currentlySelectedId={typeof value === "string" ? value : null}
                onSelectId={(companyId: string | null) =>
                  updateFormData(path, companyId, shouldReloadSchema)
                }
                disabled={disabled || readonly}
                placeholder="Select a company"
              />
            </FieldTemplate>
          );
        }

        if (schema.format === "fpUser") {
          return (
            <FieldTemplate
              key={fieldId}
              id={fieldId}
              label={formattedLabel}
              description={schema.description}
              required={isRequired}
              error={error}
              schemaPath={path}
              formContext={formContext}
              defaultValue={getDefaultValue(schema)}
              schema={schema}
              currValue={value}
            >
              <UserSelector
                currentlySelectedId={typeof value === "string" ? value : null}
                onSelectId={(userId: string | null) =>
                  updateFormData(path, userId, shouldReloadSchema)
                }
                disabled={disabled || readonly}
                placeholder="Select a user"
              />
            </FieldTemplate>
          );
        }

        if (schema.format === "fpLocation") {
          return (
            <FieldTemplate
              key={fieldId}
              id={fieldId}
              label={formattedLabel}
              description={schema.description}
              required={isRequired}
              error={error}
              schemaPath={path}
              formContext={formContext}
              defaultValue={getDefaultValue(schema)}
              schema={schema}
              currValue={value}
            >
              <LocationSelector
                currentlySelectedId={typeof value === "string" ? value : null}
                onSelectId={(locationId: string | null) =>
                  updateFormData(path, locationId, shouldReloadSchema)
                }
                disabled={disabled || readonly}
                placeholder="Select a location"
              />
            </FieldTemplate>
          );
        }

        if (schema.format === "fpOpportunity") {
          return (
            <FieldTemplate
              key={fieldId}
              id={fieldId}
              label={formattedLabel}
              description={schema.description}
              required={isRequired}
              error={error}
              schemaPath={path}
              formContext={formContext}
              defaultValue={getDefaultValue(schema)}
              schema={schema}
              currValue={value}
            >
              <OpportunitySelector
                currentlySelectedId={typeof value === "string" ? value : null}
                onSelectId={(opportunityId: string | null) =>
                  updateFormData(path, opportunityId, shouldReloadSchema)
                }
                disabled={disabled || readonly}
                placeholder="Select an opportunity"
              />
            </FieldTemplate>
          );
        }

        if (schema.format === "fpItem") {
          return (
            <FieldTemplate
              key={fieldId}
              id={fieldId}
              label={formattedLabel}
              description={schema.description}
              required={isRequired}
              error={error}
              schemaPath={path}
              formContext={formContext}
              defaultValue={getDefaultValue(schema)}
              schema={schema}
              currValue={value}
            >
              <ItemSelector
                currentlySelectedId={typeof value === "string" ? value : null}
                onSelectId={(itemId) =>
                  updateFormData(path, itemId, shouldReloadSchema)
                }
                disabled={disabled || readonly}
                enableQuickCreate={!disabled && !readonly}
                placeholder="Select from inventory…"
              />
            </FieldTemplate>
          );
        }

        if (schema.format === "fpOperation") {
          return (
            <FieldTemplate
              key={fieldId}
              id={fieldId}
              label={formattedLabel}
              description={schema.description}
              required={isRequired}
              error={error}
              schemaPath={path}
              formContext={formContext}
              defaultValue={getDefaultValue(schema)}
              schema={schema}
              currValue={value}
            >
              <OperationSelector
                currentlySelectedId={typeof value === "string" ? value : null}
                onSelectId={(operationId) =>
                  updateFormData(path, operationId, shouldReloadSchema)
                }
                disabled={disabled || readonly}
                placeholder="Select from operations…"
              />
            </FieldTemplate>
          );
        }

        if (schema.format === "fpStageId") {
          return (
            <FieldTemplate
              key={fieldId}
              id={fieldId}
              label={formattedLabel}
              description={schema.description}
              required={isRequired}
              error={error}
              schemaPath={path}
              formContext={formContext}
              defaultValue={getDefaultValue(schema)}
              schema={schema}
              currValue={value}
            >
              <OpportunityStageSelector
                currentlySelectedId={typeof value === "string" ? value : null}
                onSelectId={(stageId) =>
                  updateFormData(path, stageId, shouldReloadSchema)
                }
                disabled={disabled || readonly}
                placeholder="Select a stage"
              />
            </FieldTemplate>
          );
        }

        const inputType =
          schema.format === "email"
            ? "email"
            : schema.format === "uri"
              ? "url"
              : schema.format === "date"
                ? "date"
                : schema.format === "time"
                  ? "time"
                  : schema.format === "date-time"
                    ? "datetime-local"
                    : "text";
        return (
          <FieldTemplate
            key={fieldId}
            id={fieldId}
            label={formattedLabel}
            description={schema.description}
            required={isRequired}
            error={error}
            schemaPath={path}
            formContext={formContext}
            defaultValue={getDefaultValue(schema)}
            schemaFormat={schema.format}
            schema={schema}
            currValue={value}
          >
            {schema.maxLength && schema.maxLength > 100 ? (
              <Textarea
                id={fieldId}
                value={typeof value === "string" ? value : ""}
                onChange={(e) =>
                  updateFormData(path, e.target.value, shouldReloadSchema)
                }
                disabled={disabled || readonly}
                maxLength={schema.maxLength}
                className="text-xs min-h-[60px]"
              />
            ) : (
              <Input
                id={fieldId}
                type={inputType}
                value={typeof value === "string" ? value : ""}
                onChange={(e) =>
                  updateFormData(path, e.target.value, shouldReloadSchema)
                }
                disabled={disabled || readonly}
                maxLength={schema.maxLength}
                className={inputCls}
              />
            )}
          </FieldTemplate>
        );
      }

      case "number":
      case "integer": {
        const hasRange =
          schema.minimum !== undefined && schema.maximum !== undefined;

        // The suffix (e.g. "lbs") sits to the right of the input and reads
        // naturally, instead of cluttering the label.
        const titleStr =
          typeof schema.title === "string" ? schema.title : undefined;
        const suffix = schema.suffix;
        const numberLabel = hideLabel
          ? undefined
          : (titleStr ?? formattedLabel);

        return (
          <FieldTemplate
            key={fieldId}
            id={fieldId}
            label={numberLabel}
            description={schema.description}
            required={isRequired}
            error={error}
            schemaPath={path}
            formContext={formContext}
            defaultValue={getDefaultValue(schema)}
            schema={schema}
            currValue={value}
          >
            {hasRange && schema.maximum! - schema.minimum! <= 100 ? (
              <div className="space-y-1">
                <Slider
                  id={fieldId}
                  min={schema.minimum}
                  max={schema.maximum}
                  step={type === "integer" ? 1 : 0.1}
                  value={[
                    typeof value === "number" ? value : schema.minimum || 0,
                  ]}
                  onValueChange={([val]) =>
                    updateFormData(path, val, shouldReloadSchema)
                  }
                  disabled={disabled || readonly}
                />
                <div
                  className={cn("text-muted-foreground text-center", "text-xs")}
                >
                  {typeof value === "number" ? value : schema.minimum || 0}
                </div>
              </div>
            ) : (
              <InputWithSuffix suffix={suffix}>
                <NumberTextInput
                  id={fieldId}
                  value={value}
                  isInteger={type === "integer"}
                  minimum={schema.minimum}
                  maximum={schema.maximum}
                  onChange={(val) =>
                    updateFormData(path, val, shouldReloadSchema)
                  }
                  disabled={disabled || readonly}
                  className="h-full rounded-none border-0 bg-transparent shadow-none focus-visible:ring-0"
                />
              </InputWithSuffix>
            )}
          </FieldTemplate>
        );
      }

      case "boolean":
        return (
          <FieldTemplate
            key={fieldId}
            id={fieldId}
            label={formattedLabel}
            description={schema.description}
            required={isRequired}
            error={error}
            schemaPath={path}
            formContext={formContext}
            defaultValue={getDefaultValue(schema)}
            schema={schema}
            currValue={value}
          >
            <div>
              <Switch
                id={fieldId}
                checked={typeof value === "boolean" ? value : false}
                onCheckedChange={(checked) => {
                  updateFormData(path, checked, shouldReloadSchema);
                }}
                disabled={disabled || readonly}
              />
            </div>
          </FieldTemplate>
        );

      case "null":
        return (
          <div key={fieldId} className="text-sm text-muted-foreground">
            Set to empty
          </div>
        );

      default:
        return (
          <div key={fieldId}>
            <Alert>
              <AlertDescription>
                Unsupported field type: {type}
              </AlertDescription>
            </Alert>
          </div>
        );
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm(formData)) {
      onSubmit?.(formData);
    }
  };

  // Arrow Up/Down jumps between the form's text/number inputs for fast data
  // entry (Tab still works too). Skips selects/comboboxes/textareas, where the
  // arrow keys have their own meaning, and ignores portaled popovers (their
  // inputs aren't part of this form's input list).
  const handleArrowNav = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") {
      return;
    }
    const target = e.target as HTMLElement;
    if (
      target.tagName !== "INPUT" ||
      ((target as HTMLInputElement).type !== "text" &&
        (target as HTMLInputElement).type !== "number")
    ) {
      return;
    }
    const inputs = Array.from(
      e.currentTarget.querySelectorAll<HTMLInputElement>(
        'input:not([type="hidden"]):not([disabled]):not([readonly])',
      ),
    ).filter((i) => i.type === "text" || i.type === "number");
    const index = inputs.indexOf(target as HTMLInputElement);
    if (index === -1) {
      return;
    }
    const next = inputs[e.key === "ArrowDown" ? index + 1 : index - 1];
    if (next) {
      e.preventDefault();
      next.focus();
      next.select();
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      onKeyDown={handleArrowNav}
      className={cn("space-y-3", className)}
    >
      {renderField(schema, [], undefined, schema.required)}
      {showSubmitButton && (
        <Button type="submit" disabled={disabled}>
          Submit
        </Button>
      )}
    </form>
  );
}
